import Header from '../components/Header';
import Footer from '../components/Footer';
import AccountCard from '../components/AccountCard';
import { CATEGORY_TABS, FREE_FIRE_BANNER, SERVERS, SORT_OPTIONS } from '../mock';
import { ChevronRight, Filter, LayoutGrid, Share2, DollarSign, ChevronDown, Loader2, Package } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { accountsApi } from '../lib/api';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

const AccountsGrid = ({ loading, accounts }) => {
  if (loading) {
    return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-300" /></div>;
  }
  if (accounts.length === 0) {
    return (
      <div className="bg-[#111b38] border border-dashed border-white/10 rounded-xl p-16 text-center">
        <Package className="h-14 w-14 mx-auto text-slate-500 mb-4" />
        <h3 className="text-xl font-semibold text-white">No accounts available yet</h3>
        <p className="text-slate-400 mt-2">Check back soon — sellers are uploading fresh Free Fire accounts.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {accounts.map((acc) => (
        <AccountCard key={acc.id} account={acc} />
      ))}
    </div>
  );
};

const Marketplace = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [server, setServer] = useState('All Server');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [sort, setSort] = useState('newest');
  const [priceRange, setPriceRange] = useState([0, 200]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    accountsApi.list({ server, online_only: onlineOnly, sort })
      .then(({ data }) => { if (!cancel) setAccounts(data); })
      .catch(() => { if (!cancel) setAccounts([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [server, onlineOnly, sort]);

  const filtered = useMemo(
    () => accounts.filter((a) => a.price >= priceRange[0] && a.price <= priceRange[1]),
    [accounts, priceRange]
  );

  return (
    <div className="bg-marketplace min-h-screen">
      <Header />

      {/* Hero banner */}
      <section className="relative overflow-hidden">
        <div
          className="h-56 lg:h-72 w-full bg-cover bg-center relative"
          style={{ backgroundImage: `url(${FREE_FIRE_BANNER})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1428]/40 via-transparent to-[#0a1428]" />
          <div className="absolute top-3 right-4 flex gap-2">
            <button className="h-9 w-9 rounded-full bg-black/40 hover:bg-black/60 grid place-items-center border border-white/10">
              <Share2 className="h-4 w-4 text-white" />
            </button>
            <button className="h-9 w-9 rounded-full bg-black/40 hover:bg-black/60 grid place-items-center border border-white/10">
              <DollarSign className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 -mt-14 relative z-10">
          <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
            <LayoutGrid className="h-4 w-4" />
            <span className="hover:text-amber-300 cursor-pointer">All markets</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="hover:text-amber-300 cursor-pointer">Free Fire</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-amber-300">Accounts</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tight flex items-center gap-3">
            <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-400 bg-clip-text text-transparent">Free Fire</span>
            <ChevronRight className="h-6 w-6 text-slate-400" />
            <span className="text-white">Accounts</span>
          </h1>
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-6">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-slim border-b border-white/5">
          {CATEGORY_TABS.map((t) => {
            const active = activeTab === t.key;
            const disabled = t.count === 0;
            let tabTextClass = 'text-slate-300 hover:text-white';
            if (active) tabTextClass = 'text-amber-300';
            else if (disabled) tabTextClass = 'text-slate-600 cursor-not-allowed';
            const countPillClass = active ? 'bg-amber-400/20 text-amber-200' : 'bg-white/5 text-slate-400';
            return (
              <button
                key={t.key}
                onClick={() => !disabled && setActiveTab(t.key)}
                disabled={disabled}
                className={`relative shrink-0 h-11 px-4 text-sm font-medium flex items-center gap-2 transition-colors ${tabTextClass}`}
              >
                {t.label}
                <span className={`text-[11px] px-1.5 py-0.5 rounded-md ${countPillClass}`}>
                  {t.count}
                </span>
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-300 rounded-full" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters row */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-5 flex flex-wrap items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center justify-between gap-2 h-10 px-3 min-w-[160px] rounded-md bg-white/5 border border-white/10 text-sm hover:bg-white/10">
              <span>{server}</span>
              <ChevronDown className="h-4 w-4 opacity-70" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#0f1a38] border-white/10 text-white">
            {SERVERS.map((s) => (
              <DropdownMenuItem key={s} onClick={() => setServer(s)} className="cursor-pointer focus:bg-white/10">
                {s}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Switch checked={onlineOnly} onCheckedChange={setOnlineOnly} />
            <span>Online seller</span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center justify-between gap-2 h-10 px-3 min-w-[140px] rounded-md bg-white/5 border border-white/10 text-sm hover:bg-white/10">
                <span>{SORT_OPTIONS.find((s) => s.key === sort)?.label}</span>
                <ChevronDown className="h-4 w-4 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#0f1a38] border-white/10 text-white">
              {SORT_OPTIONS.map((s) => (
                <DropdownMenuItem key={s.key} onClick={() => setSort(s.key)} className="cursor-pointer focus:bg-white/10">
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="h-10 px-4 bg-white/5 border-white/10 hover:bg-white/10 text-white">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0b1732] border-white/10 text-white w-full sm:max-w-md">
              <SheetHeader>
                <SheetTitle className="text-white">Filter Accounts</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <div>
                  <Label className="text-slate-300">Price range (USD)</Label>
                  <div className="mt-4 px-1">
                    <Slider value={priceRange} min={0} max={200} step={1} onValueChange={setPriceRange} />
                    <div className="flex justify-between mt-2 text-xs text-slate-400">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 mb-3 block">Features</Label>
                  <div className="space-y-2">
                    {['Evo Guns', 'Rare Bundles', 'High Vault (>500)', 'Heroic Rank', 'Naruto Bundle'].map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <Checkbox id={f} />
                        <label htmlFor={f} className="text-sm text-slate-300">{f}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-6">
        <AccountsGrid loading={loading} accounts={filtered} />
        <div className="mt-8 flex items-center justify-between text-sm text-slate-400">
          <span>Showing {filtered.length} of {accounts.length} results</span>
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((p) => (
              <button key={p} className={`h-8 w-8 rounded-md text-sm ${p === 1 ? 'bg-amber-400 text-slate-900 font-semibold' : 'bg-white/5 hover:bg-white/10 text-slate-300'}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Info section */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-16">
        <h2 className="text-2xl lg:text-3xl font-bold text-white">Buy & Sell Free Fire Accounts</h2>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-4xl">
          Land in Bermuda with the rarest fits in the lobby — Nagisa Gaming Store lists Free Fire accounts from verified sellers:
          evo gun maxed profiles, rare bundle collections, high-rank histories, and diamond reserves.
        </p>
        <p className="mt-3 text-slate-300 leading-relaxed max-w-4xl">
          Listings detail bundles, evo levels, rank, and binding, with screenshots before payment. Buyer protection holds your funds until handover succeeds.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
          {[
            { t: 'Account Options', items: ['Collection accounts with rare/elite bundles', 'Evo-maxed gun skin profiles', 'High-rank Heroic/Grandmaster histories'] },
            { t: 'Why Nagisa Gaming Store?', items: ['Verified sellers with public ratings', 'Funds released only after delivery', 'Secure chat plus 24/7 support'] },
            { t: 'How to Buy', items: ['Compare collections and prices', 'Confirm binding via secure chat', 'Pay protected, secure the account'] },
          ].map((b) => (
            <div key={b.t} className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-white mb-3">{b.t}</h3>
              <ul className="space-y-2 text-sm text-slate-300 list-disc pl-4">
                {b.items.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Marketplace;
